import type { TFunction } from 'i18next';
import { SAMPLE_STATUS } from '../constants/sample.constants';
import type { SampleListParams } from '../services/sample.service';
import { joinLabeledValue } from './joinLabeledValue';
import { statusToStatId } from './sampleListFilterParams';
import { formatSampleTypeDisplay } from './sampleTypeLabels';
import { formatPathogenGenusDisplay } from './pathogenGenusDisplay';

const DETAILED_FILTER_KEYS: (keyof SampleListParams)[] = [
  'preparer',
  'sampleType',
  'pathogenGenus',
  'bslLevel',
  'storageRoomId',
  'drawerNumber',
  'caseName',
  'notes',
  'notesIsEmpty',
  'status',
  'createdDate',
  'createdDateDir',
  'createdDateVal',
  'createdDateUnit',
  'lastUsedDate',
  'lastUsedDateDir',
  'lastUsedDateVal',
  'lastUsedDateUnit',
];

const LOCATION_KEYS: (keyof SampleListParams)[] = [
  'storageRoomId',
  'drawerNumber',
  'caseName',
];

const CREATED_DATE_KEYS: (keyof SampleListParams)[] = [
  'createdDate',
  'createdDateDir',
  'createdDateVal',
  'createdDateUnit',
];

const LAST_USED_DATE_KEYS: (keyof SampleListParams)[] = [
  'lastUsedDate',
  'lastUsedDateDir',
  'lastUsedDateVal',
  'lastUsedDateUnit',
];

function hasMeaningfulValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '' && value !== false;
}

function dateGroupActive(
  filters: SampleListParams,
  dateKey: 'createdDate' | 'lastUsedDate',
  valKey: 'createdDateVal' | 'lastUsedDateVal'
): boolean {
  return hasMeaningfulValue(filters[dateKey]) || filters[valKey] !== undefined;
}

/** 詳細フィルター（キーワード以外）に入っている条件数 */
export function countActiveSampleDetailedFilters(
  filters: SampleListParams,
  opts?: { hideLocationFilters?: boolean }
): number {
  let count = 0;
  for (const key of DETAILED_FILTER_KEYS) {
    if (opts?.hideLocationFilters && LOCATION_KEYS.includes(key)) continue;
    const value = filters[key];
    if (!hasMeaningfulValue(value)) continue;
    // 日付の前後・単位だけが残っている場合はカウントしない（値や日付が無いと無意味）
    if (key === 'createdDateDir' || key === 'createdDateUnit') {
      if (!dateGroupActive(filters, 'createdDate', 'createdDateVal')) continue;
    }
    if (key === 'lastUsedDateDir' || key === 'lastUsedDateUnit') {
      if (!dateGroupActive(filters, 'lastUsedDate', 'lastUsedDateVal')) continue;
    }
    if (key === 'createdDateVal' && !dateGroupActive(filters, 'createdDate', 'createdDateVal')) {
      continue;
    }
    if (key === 'lastUsedDateVal' && !dateGroupActive(filters, 'lastUsedDate', 'lastUsedDateVal')) {
      continue;
    }
    // 既定の「利用可能」は統計ストリップの表示モード（詳細フィルター件数に含めない）
    if (key === 'status' && statusToStatId(String(value)) === 'available') {
      continue;
    }
    count += 1;
  }
  return count;
}

export type SampleDetailedFilterChip = {
  id: string;
  label: string;
  keys: (keyof SampleListParams)[];
};

function statusChipLabel(status: string, t: TFunction): string {
  if (status === SAMPLE_STATUS.AVAILABLE) return t('sample_filter.status_available');
  if (status === SAMPLE_STATUS.OUT_OF_STOCK) return t('sample_filter.status_out_of_stock');
  if (status === SAMPLE_STATUS.DISPOSED) return t('sample_filter.status_disposed');
  return status;
}

function dateChipValue(
  filters: SampleListParams,
  dateKey: 'createdDate' | 'lastUsedDate',
  dirKey: 'createdDateDir' | 'lastUsedDateDir',
  valKey: 'createdDateVal' | 'lastUsedDateVal',
  unitKey: 'createdDateUnit' | 'lastUsedDateUnit',
  t: TFunction
): string {
  const parts: string[] = [];
  const date = filters[dateKey];
  if (typeof date === 'string' && date) parts.push(date);
  const val = filters[valKey];
  if (val !== undefined) {
    const dir = filters[dirKey] || 'both';
    const unit = filters[unitKey] || 'day';
    const dirLabel = t(`sample_filter.date_direction_${dir}`);
    const unitLabel = t(`sample_filter.date_unit_${unit}`);
    parts.push(`${dirLabel}${val}${unitLabel}`);
  }
  return parts.join(' · ') || '—';
}

/**
 * 詳細パネルを閉じたまま確認・解除できるチップ（日付はグループ化）。
 */
export function listSampleDetailedFilterChips(
  filters: SampleListParams,
  t: TFunction,
  opts?: {
    hideLocationFilters?: boolean;
    storageRoomLabel?: (id: number) => string;
  }
): SampleDetailedFilterChip[] {
  const chips: SampleDetailedFilterChip[] = [];
  const field = (key: string) => t(key);

  if (
    hasMeaningfulValue(filters.status) &&
    typeof filters.status === 'string' &&
    statusToStatId(filters.status) !== 'available'
  ) {
    chips.push({
      id: 'status',
      label: joinLabeledValue(field('sample_filter.status'), statusChipLabel(filters.status, t)),
      keys: ['status'],
    });
  }

  if (hasMeaningfulValue(filters.sampleType) && typeof filters.sampleType === 'string') {
    chips.push({
      id: 'sampleType',
      label: joinLabeledValue(
        field('sample.sample_type'),
        formatSampleTypeDisplay(filters.sampleType, t)
      ),
      keys: ['sampleType'],
    });
  }

  if (hasMeaningfulValue(filters.pathogenGenus) && typeof filters.pathogenGenus === 'string') {
    chips.push({
      id: 'pathogenGenus',
      label: joinLabeledValue(
        field('sample_filter.pathogen'),
        formatPathogenGenusDisplay(filters.pathogenGenus)
      ),
      keys: ['pathogenGenus'],
    });
  }

  if (hasMeaningfulValue(filters.bslLevel) && typeof filters.bslLevel === 'string') {
    chips.push({
      id: 'bslLevel',
      label: joinLabeledValue(field('sample.bsl_level'), filters.bslLevel),
      keys: ['bslLevel'],
    });
  }

  if (hasMeaningfulValue(filters.preparer) && typeof filters.preparer === 'string') {
    chips.push({
      id: 'preparer',
      label: joinLabeledValue(field('sample_filter.preparer'), filters.preparer),
      keys: ['preparer'],
    });
  }

  if (!opts?.hideLocationFilters) {
    if (filters.storageRoomId != null) {
      const id = Number(filters.storageRoomId);
      const room = opts?.storageRoomLabel?.(id) || String(id);
      chips.push({
        id: 'storageRoomId',
        label: joinLabeledValue(field('sample.storage_room'), room),
        keys: ['storageRoomId'],
      });
    }
    if (hasMeaningfulValue(filters.drawerNumber)) {
      chips.push({
        id: 'drawerNumber',
        label: joinLabeledValue(
          field('sample_filter.drawer_number'),
          String(filters.drawerNumber)
        ),
        keys: ['drawerNumber'],
      });
    }
    if (hasMeaningfulValue(filters.caseName) && typeof filters.caseName === 'string') {
      chips.push({
        id: 'caseName',
        label: joinLabeledValue(field('sample_filter.case_name'), filters.caseName),
        keys: ['caseName'],
      });
    }
  }

  if (filters.notesIsEmpty) {
    chips.push({
      id: 'notesIsEmpty',
      label: joinLabeledValue(
        field('sample_filter.filter_notes'),
        field('sample_filter.filter_notes_empty_only')
      ),
      keys: ['notesIsEmpty', 'notes'],
    });
  } else if (hasMeaningfulValue(filters.notes) && typeof filters.notes === 'string') {
    chips.push({
      id: 'notes',
      label: joinLabeledValue(field('sample_filter.filter_notes'), filters.notes),
      keys: ['notes'],
    });
  }

  if (dateGroupActive(filters, 'createdDate', 'createdDateVal')) {
    chips.push({
      id: 'createdDate',
      label: joinLabeledValue(
        field('sample_filter.created_date'),
        dateChipValue(
          filters,
          'createdDate',
          'createdDateDir',
          'createdDateVal',
          'createdDateUnit',
          t
        )
      ),
      keys: [...CREATED_DATE_KEYS],
    });
  }

  if (dateGroupActive(filters, 'lastUsedDate', 'lastUsedDateVal')) {
    chips.push({
      id: 'lastUsedDate',
      label: joinLabeledValue(
        field('sample_filter.last_used_date'),
        dateChipValue(
          filters,
          'lastUsedDate',
          'lastUsedDateDir',
          'lastUsedDateVal',
          'lastUsedDateUnit',
          t
        )
      ),
      keys: [...LAST_USED_DATE_KEYS],
    });
  }

  return chips;
}
